import type { FC } from 'react';
import classnames from 'classnames';

import wildRiftIcon from 'app/icons/videogame-wildrift.svg';
import Icon from 'app/components/Icon';
import lolIcon from 'app/icons/videogame-lol.svg';
import lolFallbackIcon from 'app/icons/videogame-lol_fallback.svg';
import owIcon from 'app/icons/videogame-ow.svg';
import dotaIcon from 'app/icons/videogame-dota2.svg';
import csIcon from 'app/icons/videogame-cs.svg';
import rocketLeagueIcon from 'app/icons/videogame-rl.svg';
import codIcon from 'app/icons/videogame-cod.svg';
import rainbowSixIcon from 'app/icons/videogame-r6.svg';
import eaSportsFCIcon from 'app/icons/videogame-ea_sports_fc.svg';
import valorantIcon from 'app/icons/videogame-valorant.svg';
import kogIcon from 'app/icons/videogame-kog.svg';
import sc2Icon from 'app/icons/videogame-starcraft2.svg';
import scBroodWarIcon from 'app/icons/videogame-starcraft_brood_war.svg';
import eSoccerIcon from 'app/icons/videogame-e_soccer.svg';
import eBasketballIcon from 'app/icons/videogame-e_basket.svg';
import eCricketIcon from 'app/icons/videogame-e_cricket.svg';
import fortniteIcon from 'app/icons/videogame-fortnite.svg';
import pubgIcon from 'app/icons/videogame-pubg.svg';
import mlbbIcon from 'app/icons/videogame-mlbb.svg';

import style from './style.scss';

// Add a no gradiant variation because some browsers has issues with svg symbol using gradiant (ex: Firefox)
const iconHrefMap: {
  [key in VideogameSlug]: {
    default: SVG;
    noGradiant: SVG;
  };
} = {
  'league-of-legends': {
    default: lolIcon,
    noGradiant: lolFallbackIcon
  },
  ow: {
    default: owIcon,
    noGradiant: owIcon
  },
  'dota-2': {
    default: dotaIcon,
    noGradiant: dotaIcon
  },
  'cs-go': {
    default: csIcon,
    noGradiant: csIcon
  },
  rl: {
    default: rocketLeagueIcon,
    noGradiant: rocketLeagueIcon
  },
  'cod-mw': {
    default: codIcon,
    noGradiant: codIcon
  },
  'r6-siege': {
    default: rainbowSixIcon,
    noGradiant: rainbowSixIcon
  },
  fifa: {
    default: eaSportsFCIcon,
    noGradiant: eaSportsFCIcon
  },
  valorant: {
    default: valorantIcon,
    noGradiant: valorantIcon
  },
  kog: {
    default: kogIcon,
    noGradiant: kogIcon
  },
  'lol-wild-rift': {
    default: wildRiftIcon,
    noGradiant: wildRiftIcon
  },
  'starcraft-2': {
    default: sc2Icon,
    noGradiant: sc2Icon
  },
  'starcraft-brood-war': {
    default: scBroodWarIcon,
    noGradiant: scBroodWarIcon
  },
  'e-soccer': {
    default: eSoccerIcon,
    noGradiant: eSoccerIcon
  },
  'e-basketball': {
    default: eBasketballIcon,
    noGradiant: eBasketballIcon
  },
  'e-cricket': {
    default: eCricketIcon,
    noGradiant: eCricketIcon
  },
  fortnite: {
    default: fortniteIcon,
    noGradiant: fortniteIcon
  },
  pubg: {
    default: pubgIcon,
    noGradiant: pubgIcon
  },
  mlbb: {
    default: mlbbIcon,
    noGradiant: mlbbIcon
  }
};

interface Props {
  slug: VideogameSlug;
  size?: number;
  className?: string;
}

const iconClassNameMap: { [key in VideogameSlug]: string } = {
  'league-of-legends': '',
  ow: style.ow,
  'dota-2': '',
  'cs-go': style.cs,
  rl: '',
  'cod-mw': '',
  'r6-siege': style.r6,
  fifa: style.fifa,
  valorant: '',
  kog: '',
  'lol-wild-rift': '',
  'starcraft-2': '',
  'starcraft-brood-war': '',
  'e-basketball': '',
  'e-soccer': '',
  'e-cricket': '',
  fortnite: '',
  pubg: '',
  mlbb: ''
};

const VideogameIcon: FC<Props> = ({ slug, size = 50, className }) => {
  const variation = navigator.userAgent.includes('Chrome') ? 'default' : 'noGradiant';
  const iconHref: SVG | null = iconHrefMap[slug]?.[variation] || null;
  const css = { width: size, height: size };

  return (
    <Icon className={classnames(iconClassNameMap[slug], className)} href={iconHref} style={css} />
  );
};

VideogameIcon.displayName = 'VideogameIcon';

export default VideogameIcon;
